import React, {useState} from 'react';
import {useDispatch} from 'react-redux';
import {fromPairs, map} from 'lodash';
import {Field, Form, Formik} from 'formik';
import * as Yup from 'yup';
import Modal from 'shared/components/modal';
import TextInput from 'src/components/inputs/text-input';
import Label from 'shared/components/label';
import InfoTooltip from 'shared/components/info-tooltip';
import ShutDownConfirmationModal from '../ShutDownConfirmationModal';
import {shutdown} from 'src/redux/adminUniversity/actions/university';
import FormikDatePicker from 'src/components/formik-date-picker';

interface Props {
  name: string;
  university: {
    _id: string;
  };
  show: boolean;
  onDismiss: () => void;
}

const ShutDownModal = ({name, university, show, onDismiss}: Props) => {
  const dispatch = useDispatch();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const initialValues = {
    churnYear: '',
    churnMonth: '',
    churnDay: '',
    email: '',
    contractEndYear: '',
    contractEndMonth: '',
    contractEndDay: '',
  };

  const validationSchema = Yup.object().shape({
    churnYear: Yup.number().required('Required'),
    churnMonth: Yup.number().required('Required'),
    churnDay: Yup.number().required('Required'),
    email: Yup.string().email('Invalid email').required('Required'),
    contractEndYear: Yup.number().required('Required'),
    contractEndMonth: Yup.number().required('Required'),
    contractEndDay: Yup.number().required('Required'),
  });

  const handleShutdown = (values) => {
    const data = fromPairs(map(values, (value, key) => [key, value]));
    setShowConfirmation(false);
    dispatch(shutdown(data, university._id)).then(onDismiss);
  };

  const programsPage = `/admin/university/${university._id}/scholarship_programs`;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleShutdown}
      validateOnMount
    >
      {({values, isValid}) => (
        <Modal show={show} onClose={onDismiss} className="shut-down-modal">
          <Modal.TextHeader onClose={onDismiss}>Shut Down {name}</Modal.TextHeader>
          <Modal.Body>
            <p>
              This action will{' '}
              <strong>shut down the partner and halt all earnings for all followers</strong>. If you
              want to stop new followers from earning instead, then instead add a &quot;date first
              followed&quot; criterion to their program(s) through the{' '}
              <a href={programsPage}>Edit Scholarship Programs</a> page.
            </p>
            <p>
              Shutting down this partner will create a snapshot of all current student earnings,
              which will become available on the college portal microscholarships page.
            </p>
            <Form>
              <FormikDatePicker
                label={
                  <span>
                    Churn Date
                    <InfoTooltip>
                      Shutting down this partner will be effective immediately. The churn date will
                      reflect when the partner has churned for our own records (e.g. billing).
                    </InfoTooltip>
                  </span>
                }
                lastX={2}
                yearName="churnYear"
                monthName="churnMonth"
                dayName="churnDay"
                required
              />
              <FormikDatePicker
                label="Contract End Date"
                lastX={6}
                yearName="contractEndYear"
                monthName="contractEndMonth"
                dayName="contractEndDay"
                required
              />
              <Label>
                Email
                <Field
                  name="email"
                  as={TextInput}
                  required
                  placeholder="Partner contact email"
                  type="email"
                />
              </Label>
            </Form>
          </Modal.Body>
          <Modal.Footer
            primaryButton={{
              text: 'Shut Down',
              color: 'red',
              disabled: !isValid,
              onClick: () => setShowConfirmation(true),
            }}
            secondaryButton={{
              text: 'Cancel',
              onClick: onDismiss,
              rightAligned: true,
            }}
          />
          <ShutDownConfirmationModal
            show={showConfirmation}
            onDismiss={() => setShowConfirmation(false)}
            universityName={name}
            onShutdown={() => handleShutdown(values)}
          />
        </Modal>
      )}
    </Formik>
  );
};

export default ShutDownModal;
